import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import {FONT_PROXIMA_13, FONT_PROXIMA_TITLE, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import ProgressBar from 'ui-library/bs/ProgressBar';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';
import * as RichObjectTable from 'ui-library/RichObjectTable';

import StudyDesignTabButton from '../utils/ui/StudyDesignTabButton';
import {ClinicalBasedStudyExecutionOverviewQuery_study_StudyNode_sampleSummary_SampleSummaryNode_sampleDefinitionSummaries_SampleDefinitionSummaryNode_testOrderSummaries_TestOrderSummaryNode_procedure as ProcedureOrInaccessibleInfoNode} from './__generated/ClinicalBasedStudyExecutionOverviewQuery';
import ClinicalStudyOverviewTable, {renderEmptyRow} from './ClinicalStudyOverviewTable';
import {usePublishedProcedureFlowchartVersionIdsByProcedureId} from './PublishedProcedureFlowchartVersionUtils';
import {TestOrderChips, TargetedChip, DiscrepancyChip, ReceivedChip} from './SampleTableChips';
import {
  SampleDefinitionSummary,
  SampleDefinitionReceivingSummary,
  SampleDefinitionTestOrderSummary,
  SampleTestPlanProcedure,
} from './SampleTableUtils';

interface SampleTableProps {
  studyId: string;
  studyType: StudyType;
  sampleDefinitionSummaries: Array<SampleDefinitionSummary>;
  uncategorizedSampleDefinitionSummary: SampleDefinitionReceivingSummary | null;
  sampleTestPlanProcedures: Array<SampleTestPlanProcedure>;
}

export default function SampleTable({
  studyId,
  studyType,
  sampleDefinitionSummaries,
  uncategorizedSampleDefinitionSummary,
  sampleTestPlanProcedures,
}: SampleTableProps): JSX.Element {
  const flowchartVersionIdsByProcedureId =
    usePublishedProcedureFlowchartVersionIdsByProcedureId(sampleTestPlanProcedures);

  const getFlowchartVersionId = (assay: ProcedureOrInaccessibleInfoNode): string | undefined => {
    const assayId = getAccessibleOrInaccessibleId(assay);
    return flowchartVersionIdsByProcedureId[assayId];
  };

  const getSampleDefinitionCell = (definition: string, rowSpan: number): JSX.Element => (
    <RichObjectTable.Cell rowSpan={rowSpan} className={css(styles.sampleDefinitionContainer)}>
      <div className={css(styles.sampleDefinitionText)}>{definition}</div>
    </RichObjectTable.Cell>
  );

  const getSampleReceivingCell = (
    sampleDefinitionId: string,
    {receivedCount, stillTargetedCount}: SampleDefinitionReceivingSummary,
    rowSpan: number
  ): JSX.Element => {
    const stillTargetedCountChip = (
      <TargetedChip
        studyId={studyId}
        sampleDefinitionId={sampleDefinitionId}
        stillTargetedCount={stillTargetedCount}
        receivedCount={receivedCount}
      />
    );
    const receivedCountChip = (
      <ReceivedChip studyId={studyId} sampleDefinitionId={sampleDefinitionId} receivedCount={receivedCount} />
    );
    return (
      <RichObjectTable.Cell rowSpan={rowSpan} className={css(styles.sampleReceivingContainer)}>
        <div className={css(styles.sampleReceivingChipsContainer)}>
          <span className={css(styles.sampleReceivingText)}>{receivedCountChip}</span>
          <span className={css(styles.sampleReceivingText)}>{stillTargetedCountChip}</span>
        </div>
      </RichObjectTable.Cell>
    );
  };

  const getDiscrepanciesCell = (
    sampleDefinitionId: string,
    discrepancyCount: number,
    rowSpan: number
  ): JSX.Element => {
    const discrepancyCountChip = (
      <DiscrepancyChip
        studyId={studyId}
        sampleDefinitionId={sampleDefinitionId}
        discrepancyCount={discrepancyCount}
      />
    );
    return (
      <RichObjectTable.Cell rowSpan={rowSpan} className={css(styles.sampleReceivingContainer)}>
        <span className={css(styles.sampleReceivingText)}>{discrepancyCountChip}</span>
      </RichObjectTable.Cell>
    );
  };

  const getTestingProgressCell = (
    sampleDefinitionId: string,
    assayId: string,
    receivedCount: number,
    resolvedCount: number,
    totalCount: number
  ): JSX.Element => {
    const percent = totalCount > 0 ? Math.floor((resolvedCount / totalCount) * 100) : 0;
    const testOrderChips = (
      <TestOrderChips
        studyId={studyId}
        sampleDefinitionId={sampleDefinitionId}
        assayId={assayId}
        receivedCount={receivedCount}
        resolvedCount={resolvedCount}
        totalCount={totalCount}
      />
    );
    return (
      <RichObjectTable.Cell className={css(styles.progressCellContainer)}>
        <div className={css(styles.progressContainer)}>
          {totalCount > 0 && (
            <>
              <ProgressBar type="onboarding" className={css(styles.progressBar)} value={percent} />
              <div className={css(styles.percentText)}>{`${percent}%`}</div>
            </>
          )}
          {testOrderChips}
        </div>
      </RichObjectTable.Cell>
    );
  };

  const getAssayCell = (assay: ProcedureOrInaccessibleInfoNode): JSX.Element => (
    <RichObjectTable.Cell className={css(styles.sampleCell)}>
      <ProcedureChip
        id={getAccessibleOrInaccessibleId(assay)}
        size={ChipSize.TABLE_CELL}
        flowchartVersionId={getFlowchartVersionId(assay)}
      />
    </RichObjectTable.Cell>
  );

  const getSampleDefinitionSummaryRows = (samples: Array<SampleDefinitionSummary>): Array<JSX.Element> => {
    const rows: Array<JSX.Element> = [];
    samples.forEach((sample) => {
      // If there are no test order summaries, we still want to render the sample definition row with the definition and receiving columns.
      // We assign the test order to be null to trigger the rendering of the empty cell.
      const testOrderSummaries: Array<SampleDefinitionTestOrderSummary | null> =
        sample.testOrderSummaries.length > 0 ? sample.testOrderSummaries : [null];
      const rowSpan = testOrderSummaries.length > 0 ? testOrderSummaries.length : 1;

      // Used to determine if the current assay is the first one for the sample, for row spanning purposes
      let isFirstAssayForSample = true;
      testOrderSummaries.forEach((testOrderSummary) => {
        const sampleDefinitionCell = isFirstAssayForSample
          ? getSampleDefinitionCell(sample.sampleDefinitionLabel, rowSpan)
          : null;
        const sampleReceivingCell = isFirstAssayForSample
          ? getSampleReceivingCell(sample.sampleDefinitionId, sample.receiving, rowSpan)
          : null;
        const discrepancyCell = isFirstAssayForSample
          ? getDiscrepanciesCell(sample.sampleDefinitionId, sample.receiving.discrepancyCount, rowSpan)
          : null;
        const assayCell = testOrderSummary ? (
          getAssayCell(testOrderSummary.assay)
        ) : (
          <RichObjectTable.Cell className={css(styles.emptyCell)} />
        );
        const sampleTestingProgressCell = testOrderSummary ? (
          getTestingProgressCell(
            sample.sampleDefinitionId,
            getAccessibleOrInaccessibleId(testOrderSummary.assay),
            sample.receiving.receivedCount,
            testOrderSummary.resolvedCount,
            testOrderSummary.totalCount
          )
        ) : (
          <RichObjectTable.Cell className={css(styles.emptyCell)} />
        );
        isFirstAssayForSample = false;
        rows.push(
          <RichObjectTable.Row key={rows.length + 1}>
            {sampleDefinitionCell}
            {sampleReceivingCell}
            {discrepancyCell}
            {assayCell}
            {sampleTestingProgressCell}
          </RichObjectTable.Row>
        );
      });
    });
    return rows;
  };

  const getUncategorizedSampleDefinitionSummaryRow = (
    sampleSummary: SampleDefinitionReceivingSummary | null
  ): JSX.Element | null => {
    if (!sampleSummary) {
      return null;
    }
    const {receivedCount, discrepancyCount} = sampleSummary;
    if (receivedCount === 0) {
      return null;
    }
    return (
      <RichObjectTable.Row key={0}>
        <RichObjectTable.Cell className={css(styles.sampleDefinitionContainer)}>
          <div
            className={classNames(
              css(styles.sampleDefinitionText),
              css(styles.uncategorizedSampleDefinitionText)
            )}
          >
            Uncategorized samples
          </div>
        </RichObjectTable.Cell>
        <RichObjectTable.Cell className={css(styles.sampleReceivingContainer)}>
          <span className={css(styles.sampleReceivingText)}>
            <ReceivedChip studyId={studyId} sampleDefinitionId={null} receivedCount={receivedCount} />
          </span>
        </RichObjectTable.Cell>
        <RichObjectTable.Cell className={css(styles.sampleReceivingContainer)}>
          <span className={css(styles.sampleReceivingText)}>
            <DiscrepancyChip
              studyId={studyId}
              sampleDefinitionId={null}
              discrepancyCount={discrepancyCount}
            />
          </span>
        </RichObjectTable.Cell>
        <RichObjectTable.Cell className={css(styles.emptyCell)} />
        <RichObjectTable.Cell className={css(styles.emptyCell)} />
      </RichObjectTable.Row>
    );
  };

  const uncategorizedSampleDefinitionSummaryRow = getUncategorizedSampleDefinitionSummaryRow(
    uncategorizedSampleDefinitionSummary
  );
  const sampleDefinitionSummaryRows = getSampleDefinitionSummaryRows(sampleDefinitionSummaries);

  const tableRows = uncategorizedSampleDefinitionSummaryRow
    ? [uncategorizedSampleDefinitionSummaryRow, ...sampleDefinitionSummaryRows]
    : sampleDefinitionSummaryRows;

  const isEmpty = sampleDefinitionSummaryRows.length === 0 && !uncategorizedSampleDefinitionSummaryRow;
  if (isEmpty) {
    tableRows.push(
      renderEmptyRow(
        'Samples will show up here once they are defined',
        <StudyDesignTabButton studyId={studyId} studyType={studyType} />
      )
    );
  }

  return (
    <ClinicalStudyOverviewTable
      title="Samples"
      columnNames={!isEmpty ? SAMPLE_TABLE_COLUMNS : []}
      tableRows={tableRows}
    />
  );
}

/* exported for testing */
export const SAMPLE_TABLE_COLUMNS = [
  {
    name: 'Sample definition',
    width: 18,
  },
  {
    name: 'Sample receiving',
    width: 22,
  },
  {
    name: 'Discrepancies',
    width: 14,
  },
  {
    name: 'Assay',
    width: 16,
  },
  {
    name: 'Sample testing progress',
    width: 30,
  },
];

const styles = StyleSheet.create({
  emptyCell: {
    backgroundColor: Color.GRAY_50,
  },
  sampleCell: {
    paddingRight: 0,
    paddingBottom: 4,
    paddingLeft: 12,
    paddingTop: 4,
  },
  sampleDefinitionContainer: {
    paddingBottom: 3,
    paddingTop: 3,
    paddingLeft: 12,
  },
  sampleReceivingContainer: {
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 12,
  },
  sampleReceivingChipsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 6,
  },
  percentText: {
    textAlign: 'left',
    flexGrow: 0,
    width: 32,
    ...FONT_PROXIMA_TITLE,
    marginRight: 12,
  },
  progressBar: {
    height: 8,
    radius: 8,
    marginRight: 4,
    marginBottom: 4,
    marginTop: 4,
    width: 120,
    textAlign: 'left',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressCellContainer: {
    paddingRight: 6,
    paddingBottom: 6,
    paddingLeft: 12,
    paddingTop: 6,
  },
  sampleReceivingText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_700,
  },
  sampleDefinitionText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    color: Color.GRAY_900,
    lineHeight: '26px',
  },
  uncategorizedSampleDefinitionText: {
    fontStyle: 'italic',
  },
});
